/**
 * 스타일 셰이더 — PBR을 버리지 않고 그 위에 얹는다.
 *
 * 셀 셰이딩을 하겠다고 MeshToonMaterial로 갈아타면 노멀맵·AO·메탈니스가 전부
 * 날아간다. 그래서 여기서는 `onBeforeCompile`로 MeshPhysicalMaterial의 셰이더에
 * 직접 코드를 끼워 넣는다. 물리 계산은 three가 그대로 하고, 우리는 **결과 색만**
 * 손본다.
 *
 * 끼워 넣는 위치가 중요하다. `<tonemapping_fragment>` **앞**이다.
 * 그 뒤로 가면 이미 톤매핑과 sRGB 변환이 끝난 색이라, 림라이트를 더해도
 * 하이라이트가 물리적으로 어긋나고 톤매핑을 못 탄다.
 */

import * as THREE from 'three';

export interface StyleUniforms {
  uToonSteps: { value: number };
  uToonMix: { value: number };
  uRimColor: { value: THREE.Color };
  uRimStrength: { value: number };
  uRimPower: { value: number };
  uIridStrength: { value: number };
  uBackTint: { value: number };
  /** 1 이면 N뷰 투영(턴어라운드 모드). 0 이면 보통 uv. */
  uTriplanar: { value: number };
  uBlendPow: { value: number };
  /**
   * N뷰 투영 파라미터. 배열 유니폼(vec4[8]) 대신 8×2 부동소수 텍스처에 담는다 —
   * 배열 유니폼은 첫 원소만 올라가는 드라이버 조합이 실측됐다(ANGLE/D3D11, 2026-09-03).
   * 행 0: [rx, rz, 1/w, 0], 행 1: [u0, v0, du, dv].
   */
  uProjCount: { value: number };
  uProjTex: { value: THREE.DataTexture };
  uProjOffset: { value: THREE.Vector3 };
  uProjScale: { value: number };
  /** 1 이면 뷰 가중치를 색으로 그린다(디버그, ?debugproj=1). */
  uProjDebug: { value: number };
}

export const PROJ_MAX_VIEWS = 8;

function makeProjTexture(): THREE.DataTexture {
  const tex = new THREE.DataTexture(new Float32Array(PROJ_MAX_VIEWS * 2 * 4), PROJ_MAX_VIEWS, 2, THREE.RGBAFormat, THREE.FloatType);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

export function createStyleUniforms(): StyleUniforms {
  return {
    uToonSteps: { value: 4 },
    uToonMix: { value: 0 },
    uRimColor: { value: new THREE.Color('#7fd4ff') },
    uRimStrength: { value: 0.25 },
    uRimPower: { value: 2.6 },
    uIridStrength: { value: 0 },
    uBackTint: { value: 0.35 },
    uTriplanar: { value: 0 },
    // 가시성 마스크가 붙은 뒤로는 더 날카롭게 섞어도 된다 — 2.5 에서는 뷰 둘이 겹쳐 보이는 이중상이 남았다.
    uBlendPow: { value: 12.0 },
    uProjCount: { value: 0 },
    uProjTex: { value: makeProjTexture() },
    uProjOffset: { value: new THREE.Vector3() },
    uProjScale: { value: 0.5 },
    uProjDebug: { value: typeof location !== 'undefined' ? Number((location.search.match(/debugproj=(\d)/) ?? [])[1] ?? 0) : 0 },
  };
}

const VERTEX_HEAD = /* glsl */ `
attribute float aShell;
attribute vec3 aSmoothN;
attribute vec4 aVisA;
attribute vec4 aVisB;
varying float vShell;
varying vec3 vObjN;
varying vec3 vObjPos;
varying vec4 vVisA;
varying vec4 vVisB;
`;

// position/normal 은 스키닝 전 바인드 포즈다. 투영 텍스처는 바인드 포즈에 붙어야
// 팔을 들어도 텍스처가 따라간다.
const VERTEX_BODY = /* glsl */ `
vShell = aShell;
// aSmoothN 이 없는 지오메트리(단일 뷰·워밍업)는 0 → 그때는 normal 을 쓴다.
vObjN = dot(aSmoothN, aSmoothN) > 0.5 ? aSmoothN : normal;
vObjPos = position;
// 가시성 속성이 없는 지오메트리는 0 → 셰이더에서 "전부 보임"으로 취급한다.
vVisA = aVisA;
vVisB = aVisB;
`;

const FRAGMENT_HEAD = /* glsl */ `
uniform float uToonSteps;
uniform float uToonMix;
uniform vec3  uRimColor;
uniform float uRimStrength;
uniform float uRimPower;
uniform float uIridStrength;
uniform float uBackTint;
uniform float uTriplanar;
uniform float uBlendPow;
uniform int   uProjCount;
uniform sampler2D uProjTex;
uniform vec3  uProjOffset;
uniform float uProjScale;
uniform float uProjDebug;
varying float vShell;
varying vec3 vObjN;
varying vec3 vObjPos;
varying vec4 vVisA;
varying vec4 vVisB;

// 박막 간섭의 값싼 흉내. 코사인 팔레트라 부드럽게 순환한다.
vec3 tfSpectrum(float t) {
  return 0.5 + 0.5 * cos(6.28318530718 * (vec3(0.0, 0.33, 0.67) + t));
}
`;

const FRAGMENT_BODY = /* glsl */ `
{
  // normal은 노멀맵이 반영된 최종 법선이다. vNormal보다 이쪽이 결이 산다.
  vec3 tfN = normalize(normal);
  vec3 tfV = normalize(vViewPosition);
  float tfNdV = clamp(dot(tfN, tfV), 0.0, 1.0);
  float tfFresnel = pow(1.0 - tfNdV, uRimPower);

  // 셀 셰이딩: 색조는 두고 밝기만 계단으로 만든다.
  if (uToonMix > 0.001) {
    float tfL = dot(gl_FragColor.rgb, vec3(0.2126, 0.7152, 0.0722));
    if (tfL > 1e-4) {
      float tfSteps = max(2.0, uToonSteps);
      float tfQ = floor(tfL * tfSteps + 0.5) / tfSteps;
      gl_FragColor.rgb *= mix(1.0, tfQ / tfL, uToonMix);
    }
  }

  if (uIridStrength > 0.001) {
    gl_FragColor.rgb += tfSpectrum(tfFresnel * 1.2 + tfNdV * 0.35) * tfFresnel * uIridStrength;
  }

  gl_FragColor.rgb += uRimColor * tfFresnel * uRimStrength;

  // 뒷껍질은 앞면 텍스처를 거울처럼 쓰고 있다. 어둡게 눌러 티를 덜 낸다.
  gl_FragColor.rgb *= mix(1.0, 1.0 - uBackTint, clamp(vShell, 0.0, 1.0));
}
`;

/**
 * 턴어라운드 모드의 albedo 샘플링 — N뷰 투영 블렌드.
 *
 * 정점의 바인드 포즈 위치를 각 뷰의 화면에 투영해 아틀라스 칸에서 색을 읽고,
 * 법선이 그 뷰 카메라를 얼마나 정면으로 보느냐(dot^k)로 섞는다. 뷰 하나만 고르면
 * 뷰 사이 조명 차이가 이음매로 서고, 섞으면 녹는다. 위·아래를 보는 면은 수평
 * 성분으로 판단한다 — 그 방향 뷰가 없으므로 가장 가까운 것들의 평균이 된다.
 */
const MAP_FRAGMENT = /* glsl */ `
#ifdef USE_MAP
  vec4 sampledDiffuseColor;
  if (uTriplanar > 0.5 && uProjCount > 0) {
    vec3 tfP = (vObjPos - uProjOffset) * uProjScale;
    vec3 tfN = normalize(vObjN);
    vec2 tfH = vec2(tfN.x, tfN.z);
    float tfHL = length(tfH);
    tfH = tfHL > 1e-4 ? tfH / tfHL : vec2(0.0, 1.0);
    vec4 tfAcc = vec4(0.0);
    vec3 tfDbg = vec3(0.0);
    float tfWSum = 0.0;
    // 가시성 — 속성이 전부 0 이면(단일 뷰·옛 지오메트리) 쓰지 않는다.
    float tfVis[8];
    tfVis[0] = vVisA.x; tfVis[1] = vVisA.y; tfVis[2] = vVisA.z; tfVis[3] = vVisA.w;
    tfVis[4] = vVisB.x; tfVis[5] = vVisB.y; tfVis[6] = vVisB.z; tfVis[7] = vVisB.w;
    bool tfHasVis = dot(vVisA, vec4(1.0)) + dot(vVisB, vec4(1.0)) > 1e-3;
    for (int i = 0; i < 8; i++) {
      if (i >= uProjCount) break;
      vec4 tfR = texelFetch(uProjTex, ivec2(i, 0), 0);
      vec4 tfRect = texelFetch(uProjTex, ivec2(i, 1), 0);
      // 카메라 방향 d 는 r 에서 나온다: r=(cosθ,−sinθ) → d=(sinθ,cosθ) = (−r.y, r.x)
      vec2 tfD = vec2(-tfR.y, tfR.x);
      float tfW = pow(max(0.0, dot(tfH, tfD)), uBlendPow) + 1e-4;
      // 가린 뷰는 거의 빼고(0.03), 모든 뷰가 가린 정점은 정규화가 원래 비율로 되돌린다.
      if (tfHasVis) tfW *= 0.03 + 0.97 * tfVis[i];
      float tfS = (tfP.x * tfR.x + tfP.z * tfR.y) * tfR.z + 0.5;
      float tfT = tfP.y;
      vec2 tfUv = tfRect.xy + vec2(clamp(tfS, 0.0, 1.0), clamp(tfT, 0.0, 1.0)) * tfRect.zw;
      tfAcc += texture2D(map, tfUv) * tfW;
      tfWSum += tfW;
      tfDbg += tfW * (i == 0 ? vec3(1.0, 0.0, 0.0) : i == 1 ? vec3(0.0, 1.0, 0.0) : i == 2 ? vec3(0.0, 0.0, 1.0) : vec3(1.0, 1.0, 0.0));
    }
    sampledDiffuseColor = tfAcc / max(tfWSum, 1e-4);
    if (uProjDebug > 0.5 && uProjDebug < 1.5) sampledDiffuseColor = vec4(tfDbg / max(tfWSum, 1e-4), 1.0);
    if (uProjDebug > 1.5 && uProjDebug < 2.5) sampledDiffuseColor = vec4(abs(texelFetch(uProjTex, ivec2(1, 0), 0).xyz), 1.0);
    if (uProjDebug > 2.5 && uProjDebug < 3.5) sampledDiffuseColor = vec4(abs(normalize(vObjN)), 1.0);
    if (uProjDebug > 3.5 && uProjDebug < 4.5) sampledDiffuseColor = vec4(vec3(float(uProjCount) / 8.0), 1.0);
    if (uProjDebug > 4.5 && uProjDebug < 5.5) { vec4 r1 = texelFetch(uProjTex, ivec2(1, 0), 0); sampledDiffuseColor = vec4(vec3(max(0.0, dot(tfH, vec2(-r1.y, r1.x)))), 1.0); }
    if (uProjDebug > 5.5 && uProjDebug < 6.5) sampledDiffuseColor = vec4(abs(tfH.x), abs(tfH.y), 0.0, 1.0);
    // 7: 정면(뷰 0) 가시성. 팔 뒤 옆구리가 검게 나와야 맞다.
    if (uProjDebug > 6.5 && uProjDebug < 7.5) sampledDiffuseColor = vec4(vec3(tfVis[0]), 1.0);
  } else {
    sampledDiffuseColor = texture2D(map, vMapUv);
  }
  #ifdef DECODE_VIDEO_TEXTURE
    sampledDiffuseColor = sRGBTransferEOTF(sampledDiffuseColor);
  #endif
  diffuseColor *= sampledDiffuseColor;
#endif
`;

/**
 * 머티리얼에 스타일 패치를 붙인다.
 * 유니폼 객체를 그대로 공유하므로, 호출부는 `uniforms.uToonMix.value = 0.5` 처럼
 * 값만 바꾸면 된다 — 셰이더 재컴파일이 없다. 이게 슬라이더를 60fps로 만든다.
 */
export function attachStyle(
  material: THREE.MeshStandardMaterial,
  uniforms: StyleUniforms,
): void {
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);

    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${VERTEX_HEAD}`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>\n${VERTEX_BODY}`);

    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${FRAGMENT_HEAD}`)
      .replace('#include <map_fragment>', MAP_FRAGMENT)
      .replace('#include <tonemapping_fragment>', `${FRAGMENT_BODY}\n#include <tonemapping_fragment>`);
  };

  // 이 키가 없으면 three가 패치 안 된 셰이더와 캐시를 섞어 쓴다.
  material.customProgramCacheKey = () => 'texel-forge/stylize@9';
  // 평면 셰이딩이 켜지면 vNormal이 사라져 위 코드가 컴파일되지 않는다.
  material.flatShading = false;
  material.needsUpdate = true;
}
